/**
 * AutomationRuleBuilderView — pure presentation for the rule builder modal
 * (SE-5506).
 *
 * The Kind decides which shape the middle of the form takes:
 *   - **Automation** — a trigger picker (+ its stage), an action picker (+ its
 *     target stage / tag / terminal outcome);
 *   - **Rule/Guard** — a single "stage to guard" picker (the trigger + action
 *     are implicit: enter-stage → review queue).
 *
 * The Kind is FIXED before the form opens — the panel's "+ Rule" / "+ Automation"
 * buttons choose it, and editing an existing entry derives it from that entry.
 * There is no picker here: it asked the user to answer a question they had
 * already answered, and let them contradict it mid-form.
 * Below either shape sits the shared AND-combined conditions list and the LIVE
 * plain-language preview, which updates on every change before the rule is saved.
 * @remarks View component — no business logic, hooks, or state.
 */

import { Plus } from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalHeader,
} from "@/components/atoms/Modal";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import AutomationConditionRow from "../../molecules/AutomationConditionRow";
import RuleSelectField, {
  type RuleSelectOption,
} from "../../molecules/RuleSelectField";
import type { AutomationRuleConditionForm } from "../../../types/automationRules";

/** Props for {@link AutomationRuleBuilderView}. */
export interface AutomationRuleBuilderViewProps {
  /** Whether the modal is open. */
  readonly isOpen: boolean;
  /** Close request handler. */
  readonly onClose: () => void;
  /** Modal title ("New rule" / "Edit rule"). */
  readonly title: string;
  /** Whether the small-screen layout is active. */
  readonly isMobile: boolean;

  // Kind
  /**
   * Which shape the form takes. FIXED for the life of the form — set by the
   * entry point that opened it, or by the rule being edited. Not user-editable
   * here, which is why there is no setter beside it.
   */
  readonly kind: "automation" | "guard";

  // Name
  /** Rule name. */
  readonly name: string;
  /** Name change handler. */
  readonly onNameChange: (value: string) => void;

  // Automation fields
  /** Trigger options (full enum). */
  readonly triggerOptions: readonly RuleSelectOption[];
  /** Selected trigger. */
  readonly triggerValue: string;
  /** Trigger change handler. */
  readonly onTriggerChange: (value: string) => void;
  /** Whether the trigger needs a stage. */
  readonly showTriggerStage: boolean;
  /** Selected trigger stage. */
  readonly triggerStageValue: string;
  /** Trigger-stage change handler. */
  readonly onTriggerStageChange: (value: string) => void;
  /** Action options (full enum). */
  readonly actionOptions: readonly RuleSelectOption[];
  /** Selected action. */
  readonly actionValue: string;
  /** Action change handler. */
  readonly onActionChange: (value: string) => void;
  /** Whether the action needs a target stage. */
  readonly showTargetStage: boolean;
  /** Selected target stage. */
  readonly targetStageValue: string;
  /** Target-stage change handler. */
  readonly onTargetStageChange: (value: string) => void;
  /** Whether the action needs a tag. */
  readonly showTag: boolean;
  /** Tag options for apply/remove-tag actions. */
  readonly tagOptions: readonly RuleSelectOption[];
  /** Selected tag. */
  readonly tagValue: string;
  /** Tag change handler. */
  readonly onTagChange: (value: string) => void;
  /** Whether a terminal outcome selector should show. */
  readonly showOutcome: boolean;
  /** Outcome options (board.validOutcomes). */
  readonly outcomeOptions: readonly RuleSelectOption[];
  /** Selected outcome. */
  readonly outcomeValue: string;
  /** Outcome change handler. */
  readonly onOutcomeChange: (value: string) => void;

  // Guard field
  /** Stage options for the "stage to guard" picker. */
  readonly stageOptions: readonly RuleSelectOption[];
  /** Selected guard stage (mapped to triggerKanbanPhaseId). */
  readonly guardStageValue: string;
  /** Guard-stage change handler. */
  readonly onGuardStageChange: (value: string) => void;

  // Conditions
  /** The conditions being edited. */
  readonly conditions: readonly AutomationRuleConditionForm[];
  /** Condition-type options (full enum). */
  readonly conditionTypeOptions: readonly RuleSelectOption[];
  /** Comparator options (full enum). */
  readonly comparatorOptions: readonly RuleSelectOption[];
  /** Add-condition handler. */
  readonly onAddCondition: () => void;
  /** Patch a condition (key-addressed). */
  readonly onConditionChange: (
    key: string,
    patch: Partial<AutomationRuleConditionForm>
  ) => void;
  /** Remove a condition (key-addressed). */
  readonly onConditionRemove: (key: string) => void;

  // Preview + save
  /** Live plain-language preview text. */
  readonly preview: string;
  /** Whether the rule can be saved. */
  readonly canSave: boolean;
  /** Whether a save is in flight. */
  readonly isSaving: boolean;
  /** Save handler. */
  readonly onSave: () => void;
}

/**
 * Render the Automation-mode trigger + action fields.
 * @param props - The builder props.
 * @returns The automation field group.
 */
const renderAutomationFields = (props: AutomationRuleBuilderViewProps) => (
  <Stack className="gap-4">
    <RuleSelectField
      label="Trigger"
      placeholder="Choose a trigger"
      value={props.triggerValue}
      options={props.triggerOptions}
      onValueChange={props.onTriggerChange}
      testID="rule-trigger"
    />
    {props.showTriggerStage ? (
      <RuleSelectField
        label="Trigger stage"
        placeholder="Choose a stage"
        value={props.triggerStageValue}
        options={props.stageOptions}
        onValueChange={props.onTriggerStageChange}
        testID="rule-trigger-stage"
      />
    ) : null}
    <RuleSelectField
      label="Action"
      placeholder="Choose an action"
      value={props.actionValue}
      options={props.actionOptions}
      onValueChange={props.onActionChange}
      testID="rule-action"
    />
    {props.showTargetStage ? (
      <RuleSelectField
        label="Target stage"
        placeholder="Choose a stage"
        value={props.targetStageValue}
        options={props.stageOptions}
        onValueChange={props.onTargetStageChange}
        testID="rule-target-stage"
      />
    ) : null}
    {props.showTag ? (
      <RuleSelectField
        label="Tag"
        placeholder="Choose a tag"
        value={props.tagValue}
        options={props.tagOptions}
        onValueChange={props.onTagChange}
        testID="rule-tag"
      />
    ) : null}
    {props.showOutcome ? (
      <RuleSelectField
        label="Outcome (terminal stage)"
        placeholder="Choose an outcome"
        value={props.outcomeValue}
        options={props.outcomeOptions}
        onValueChange={props.onOutcomeChange}
        testID="rule-outcome"
      />
    ) : null}
  </Stack>
);

/**
 * Render the Guard-mode single stage picker.
 * @param props - The builder props.
 * @returns The guard field group.
 */
const renderGuardFields = (props: AutomationRuleBuilderViewProps) => (
  <RuleSelectField
    label="Stage to guard"
    placeholder="Choose a stage"
    value={props.guardStageValue}
    options={props.stageOptions}
    onValueChange={props.onGuardStageChange}
    testID="rule-guard-stage"
  />
);

/**
 * Render the shared conditions list + add button.
 * @param props - The builder props.
 * @returns The conditions group.
 */
const renderConditions = (props: AutomationRuleBuilderViewProps) => (
  <Stack className="gap-2">
    <Text variant="body-strong" className="text-content-secondary">
      Conditions (all must be true)
    </Text>
    {props.conditions.length === 0 ? (
      <Text variant="caption" className="text-content-muted">
        No conditions — the rule always matches.
      </Text>
    ) : (
      <Stack className="gap-2">
        {props.conditions.map((condition, index) => (
          <AutomationConditionRow
            key={condition.key}
            condition={condition}
            displayIndex={index + 1}
            conditionTypeOptions={props.conditionTypeOptions}
            comparatorOptions={props.comparatorOptions}
            stageOptions={props.stageOptions}
            onChange={props.onConditionChange}
            onRemove={props.onConditionRemove}
          />
        ))}
      </Stack>
    )}
    <Box className="items-start">
      <Button
        label="Add condition"
        variant="outline"
        tone="secondary"
        size="sm"
        icon={Plus}
        onPress={props.onAddCondition}
        accessibilityLabel="Add condition"
        testID="rule-add-condition"
      />
    </Box>
  </Stack>
);

/**
 * Render the rule builder modal.
 * @param props - {@link AutomationRuleBuilderViewProps}
 * @returns The modal.
 */
const AutomationRuleBuilderView = (props: AutomationRuleBuilderViewProps) => (
  <Modal
    isOpen={props.isOpen}
    onClose={props.isSaving ? undefined : props.onClose}
    closeOnOverlayClick
    size={props.isMobile ? "lg" : "md"}
  >
    <ModalBackdrop />
    <ModalContent testID="automation-rule-builder">
      <ModalHeader>
        <Text variant="title" className="text-content-primary">
          {props.title}
        </Text>
      </ModalHeader>
      <ModalBody>
        <ScrollView showsVerticalScrollIndicator={false}>
          <Stack className="gap-4 py-2">
            <Stack className="gap-1">
              <Text variant="body-strong" className="text-content-secondary">
                Name
              </Text>
              <Input
                testID="rule-name"
                field={{
                  value: props.name,
                  onChangeText: props.onNameChange,
                  placeholder: "Rule name",
                }}
              />
            </Stack>

            {/* No Kind picker. The kind is decided BEFORE this form opens —
                by which header button was pressed on the way in ("+ Rule" or
                "+ Automation"), or, when editing, by the entry itself. Asking
                again here made the user answer a question they had already
                answered, and let them contradict it. `kind` still switches the
                fields below; it is just no longer editable in place. */}
            {props.kind === "guard"
              ? renderGuardFields(props)
              : renderAutomationFields(props)}

            {renderConditions(props)}

            <Box className="rounded-md bg-surface-subtle p-3">
              <Text variant="caption" className="text-content-muted">
                Preview
              </Text>
              <Text
                testID="automation-rule-preview"
                variant="body"
                className="text-content-primary"
              >
                {props.preview}
              </Text>
            </Box>

            <Stack
              direction="horizontal"
              className="items-center justify-end gap-3 pt-2"
            >
              <Button
                label="Cancel"
                variant="outline"
                tone="secondary"
                size="md"
                onPress={props.onClose}
                isDisabled={props.isSaving}
                accessibilityLabel="Cancel"
              />
              <Button
                testID="rule-save"
                label="Save rule"
                variant="solid"
                tone="primary"
                size="md"
                onPress={props.onSave}
                isDisabled={!props.canSave}
                isLoading={props.isSaving}
                accessibilityLabel="Save rule"
              />
            </Stack>
          </Stack>
        </ScrollView>
      </ModalBody>
    </ModalContent>
  </Modal>
);

AutomationRuleBuilderView.displayName = "AutomationRuleBuilderView";

export default memo(AutomationRuleBuilderView);
